import React, { useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Mic, MessageSquare, Sparkles, Shield, Wrench,
  ArrowRight, ChevronDown, ChevronUp
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Changelog Definition ───
// Bump CURRENT_VERSION when deploying new features.
const CURRENT_VERSION = "2026-05-28";

const CHANGELOG = [
  {
    version: "2026-05-28",
    title: "Update — Mai 2026",
    features: [
      {
        icon: Sparkles,
        title: "GPT Image 2 — Bessere Meta Ads",
        description: "Meta Ads werden jetzt mit OpenAIs neuestem Bildmodell generiert. Schärfere Details, bessere Typografie, weniger generische Ergebnisse. Bei Ads mit Personen wird weiterhin Nano Banana verwendet.",
        isNew: true,
      },
      {
        icon: MessageSquare,
        title: "A/B Layout-Varianten",
        description: "Jede Variation hat jetzt einen eigenen Layout-Stil: Bold & Zentral, Cinematic Split, Magazin-Stil oder Street/Viral. Für echte Abwechslung statt 4x fast gleich.",
        isNew: true,
      },
      {
        icon: Shield,
        title: "Bildränder bei Story-Format",
        description: "Text und wichtige Elemente halten jetzt automatisch Abstand zu den Bildkanten — besonders bei 9:16 Story-Formaten, wo App-UI das Bild verdeckt.",
        isNew: false,
      },
      {
        icon: Wrench,
        title: "Code-Qualität & Performance",
        description: "Circular Imports aufgelöst, Legacy-Endpoints entfernt, Console-Logs bereinigt. Stabilere und schnellere App.",
        isNew: false,
      },
    ],
  },
  {
    version: "2026-03-28",
    title: "Update — März 2026",
    features: [
      {
        icon: Mic,
        title: "Spracheingabe im Wizard",
        description: "Statt tippen: Klick auf \"Einsprechen\", rede los und die KI strukturiert alles passend zum Feld. Funktioniert bei Beschreibung, Zielgruppe, Painpoints, Lösungsversprechen und Bildwünschen.",
        isNew: true,
      },
      {
        icon: MessageSquare,
        title: "Ad-Caption Generator",
        description: "Nach der Bild-Generierung: 5 verschiedene Caption-Varianten (Problem-Hook, Frage, Storytelling, Zahlen, Direkt) mit einem Klick erstellen und kopieren.",
        isNew: true,
      },
      {
        icon: Sparkles,
        title: "Recruiting-Hooks verbessert",
        description: "Bei Recruiting-Ads wird jetzt immer die Berufsbezeichnung in Hooks und Captions genannt — gegendert mit :innen oder (m/w/d).",
        isNew: false,
      },
      {
        icon: Shield,
        title: "Login-Stabilität",
        description: "Logout-Schleife nach Updates behoben. Sessions bleiben jetzt über Deployments hinweg bestehen.",
        isNew: false,
      },
      {
        icon: Wrench,
        title: "Passwort-Reset repariert",
        description: "E-Mail-Versand beim Passwort-Zurücksetzen funktioniert jetzt zuverlässig.",
        isNew: false,
      },
    ],
  },
  {
    version: "2026-03-19",
    title: "Update — 19. März 2026",
    features: [
      {
        icon: Sparkles,
        title: "Brand Kits",
        description: "Firmenfarben, Logos, CTA und Bildwünsche als Paket speichern. Im Wizard mit einem Klick anwenden.",
        link: "/brand-kits",
        linkText: "Brand Kit erstellen",
        isNew: false,
      },
      {
        icon: Sparkles,
        title: "CTA optional & Bildwünsche",
        description: "Ads ohne CTA-Button generieren und Bildwünsche direkt im Wizard beschreiben.",
        isNew: false,
      },
      {
        icon: Sparkles,
        title: "Vergleichs-Ads Multi-Format & Farb-Editor",
        description: "Mehrere Formate gleichzeitig generieren. Farben in der Style Library direkt bearbeiten.",
        isNew: false,
      },
    ],
  },
];

export const WHATS_NEW_VERSION = CURRENT_VERSION;

export default function WhatsNewModal({ onDismiss, onMarkRead }) {
  const [open, setOpen] = useState(true);
  const [expandedOlder, setExpandedOlder] = useState(false);
  const navigate = useNavigate();

  const currentChangelog = CHANGELOG.find((c) => c.version === CURRENT_VERSION);
  const olderLogs = CHANGELOG.filter((c) => c.version !== CURRENT_VERSION);

  if (!currentChangelog) return null;

  const handlePermanentDismiss = async () => {
    setOpen(false);
    try {
      await axios.put(`${API}/auth/seen-changelog`, { version: CURRENT_VERSION });
    } catch (e) {
      console.warn("Could not save changelog version:", e);
    }
    if (onMarkRead) onMarkRead();
    if (onDismiss) onDismiss();
  };

  const handleTemporaryClose = () => {
    setOpen(false);
    if (onDismiss) onDismiss();
  };

  const handleNavigate = (link) => {
    handleTemporaryClose();
    setTimeout(() => navigate(link), 100);
  };

  const renderFeature = (feature, idx) => (
    <div
      key={idx}
      className="flex gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
      data-testid={`whats-new-feature-${idx}`}
    >
      <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
        <feature.icon className="h-4 w-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{feature.title}</span>
          {feature.isNew && (
            <Badge className="text-[10px] px-1.5 py-0 h-4 bg-primary/15 text-primary border-0">
              NEU
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
          {feature.description}
        </p>
        {feature.link && (
          <button
            onClick={() => handleNavigate(feature.link)}
            className="inline-flex items-center gap-1 text-xs text-primary mt-1 hover:underline"
          >
            {feature.linkText} <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleTemporaryClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden" data-testid="whats-new-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl">{currentChangelog.title}</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-0.5">
                Neue Features und Verbesserungen
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[55vh] pr-1">
          <div className="space-y-3 py-2">
            {currentChangelog.features.map(renderFeature)}
          </div>

          {olderLogs.length > 0 && (
            <div className="mt-4">
              <button
                onClick={() => setExpandedOlder(!expandedOlder)}
                className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full py-2"
              >
                {expandedOlder ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                Ältere Updates ({olderLogs.length})
              </button>
              {expandedOlder && olderLogs.map((log) => (
                <div key={log.version} className="space-y-2 mt-2">
                  <h4 className="text-xs font-medium text-muted-foreground">{log.title}</h4>
                  {log.features.map(renderFeature)}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <Separator />

        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button onClick={handlePermanentDismiss} className="w-full" data-testid="whats-new-dismiss">
            Alles gesehen — nicht mehr anzeigen
          </Button>
          <Button variant="ghost" onClick={handleTemporaryClose} className="w-full text-muted-foreground" data-testid="whats-new-later">
            Später nochmal ansehen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
